import { useCallback, useEffect, useMemo, useState } from "react";
import {
  keepPreviousData,
  useIsFetching,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "../api/client";
import type { CompanionStatusResponse } from "../api/types";
import { AdminTokenDialog } from "../components/AdminTokenDialog";
import { AgentPanel } from "../components/AgentPanel";
import { AgentSignalsSection } from "../components/AgentSignalsSection";
import { CommandPalette } from "../components/CommandPalette";
import { CompanionSection } from "../components/CompanionSection";
import { MarketPanel } from "../components/MarketPanel";
import { SymbolEventAlarmsSection } from "../components/SymbolEventAlarmsSection";
import { SymbolPriceAlarmsSection } from "../components/SymbolPriceAlarmsSection";
import { TopBar } from "../components/TopBar";
import { useToast } from "../components/Toast";
import { Watchlist } from "../components/Watchlist";
import type { ChartType } from "../lib/chartTypes";
import { errorMessage } from "../lib/errors";
import { formatDateTime } from "../lib/format";
import { getIndicatorDefinition, type IndicatorKey } from "../lib/indicators";
import { usePersistentState } from "../lib/usePersistentState";
import { useTheme } from "../theme/ThemeProvider";

function companionSummaryText(
  status: CompanionStatusResponse | undefined,
  symbol: string | null,
): string {
  if (!status) {
    return "";
  }
  const parts = [`${status.ready_symbols || 0}/${status.tracked_symbols || 0} symbols ready`];
  const current = status.items.find((item) => item.symbol === symbol);
  if (current) {
    parts.push(`${current.ok_count || 0} ready`);
    if (current.error_count) {
      parts.push(`${current.error_count} attention`);
    }
    if (current.generated_at) {
      parts.push(`current ${formatDateTime(current.generated_at)}`);
    }
  }
  return parts.join(" - ");
}

export function ExplorerPage() {
  const queryClient = useQueryClient();
  const { resolvedTheme } = useTheme();
  const toast = useToast();

  const [selectedSymbol, setSelectedSymbol] = usePersistentState<string | null>("sa.symbol", null);
  const [range, setRange] = usePersistentState<string>("sa.range", "1Y");
  const [chartType, setChartType] = usePersistentState<ChartType>("sa.chartType", "area");
  const [activeIndicators, setActiveIndicators] = usePersistentState<IndicatorKey[]>(
    "sa.indicators",
    [],
  );
  const [recents, setRecents] = usePersistentState<string[]>("sa.recents", []);

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  const bootstrapQuery = useQuery({ queryKey: ["bootstrap"], queryFn: api.bootstrap });
  const globalFetching = useIsFetching();

  // Reconcile persisted prefs against the live bootstrap payload.
  useEffect(() => {
    const data = bootstrapQuery.data;
    if (!data) {
      return;
    }
    const tracked = data.tracked_symbols.map((item) => item.symbol);
    setSelectedSymbol((current) =>
      current && tracked.includes(current) ? current : data.default_symbol,
    );
    const rangeKeys = data.available_ranges.map((item) => item.key);
    setRange((current) =>
      rangeKeys.includes(current) ? current : data.available_ranges[0]?.key || current,
    );
    setRecents((current) => (current.length ? current : data.recent_symbols || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapQuery.data]);

  const symbolQuery = useQuery({
    queryKey: ["symbol", selectedSymbol],
    queryFn: () => api.symbol(selectedSymbol as string),
    enabled: Boolean(selectedSymbol),
    placeholderData: keepPreviousData,
  });

  const chartQuery = useQuery({
    queryKey: ["chart", selectedSymbol, range],
    queryFn: () => api.chart(selectedSymbol as string, range),
    enabled: Boolean(selectedSymbol),
    placeholderData: keepPreviousData,
  });

  const companionStatusQuery = useQuery({
    queryKey: ["companionStatus"],
    queryFn: api.companionStatus,
  });

  const companionAgentsQuery = useQuery({
    queryKey: ["companionAgents", selectedSymbol],
    queryFn: () => api.companionAgents(selectedSymbol as string),
    enabled: Boolean(selectedSymbol),
    placeholderData: keepPreviousData,
  });

  const agentSignalsQuery = useQuery({
    queryKey: ["agentSignals", selectedSymbol],
    queryFn: () => api.agentSignals(selectedSymbol as string),
    enabled: Boolean(selectedSymbol),
    placeholderData: keepPreviousData,
  });

  const alarmsQuery = useQuery({ queryKey: ["alarms"], queryFn: api.alarms });

  const [pendingAlarmAgentKey, setPendingAlarmAgentKey] = useState<string | null>(null);

  const createAlarmMutation = useMutation({
    mutationFn: ({ symbol, agentKey }: { symbol: string; agentKey: string }) =>
      api.createAlarm(symbol, agentKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alarms"] });
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Could not create the alarm."));
    },
    onSettled: () => setPendingAlarmAgentKey(null),
  });

  const deleteAlarmMutation = useMutation({
    mutationFn: (alarmId: number) => api.deleteAlarm(alarmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alarms"] });
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Could not remove the alarm."));
    },
    onSettled: () => setPendingAlarmAgentKey(null),
  });

  const eventAlarmsQuery = useQuery({ queryKey: ["eventAlarms"], queryFn: api.eventAlarms });

  const [pendingEventAlarmType, setPendingEventAlarmType] = useState<string | null>(null);

  const createEventAlarmMutation = useMutation({
    mutationFn: ({ symbol, eventType }: { symbol: string; eventType: string }) =>
      api.createEventAlarm(symbol, eventType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eventAlarms"] });
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Could not create the event alarm."));
    },
    onSettled: () => setPendingEventAlarmType(null),
  });

  const deleteEventAlarmMutation = useMutation({
    mutationFn: (alarmId: number) => api.deleteEventAlarm(alarmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eventAlarms"] });
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Could not remove the event alarm."));
    },
    onSettled: () => setPendingEventAlarmType(null),
  });

  const priceAlarmsQuery = useQuery({ queryKey: ["priceAlarms"], queryFn: api.priceAlarms });

  const [pendingPriceAlarmId, setPendingPriceAlarmId] = useState<number | null>(null);

  const createPriceAlarmMutation = useMutation({
    mutationFn: ({ symbol, targetPrice }: { symbol: string; targetPrice: number }) =>
      api.createPriceAlarm(symbol, targetPrice),
    onSuccess: (alarm) => {
      queryClient.invalidateQueries({ queryKey: ["priceAlarms"] });
      const reached = alarm.direction === "above" ? "rises to" : "falls to";
      toast.success(`Will email you when ${alarm.symbol} ${reached} $${alarm.target_price}.`);
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Could not create the price alarm."));
    },
  });

  const rearmPriceAlarmMutation = useMutation({
    mutationFn: (alarmId: number) => api.rearmPriceAlarm(alarmId),
    onSuccess: (alarm) => {
      queryClient.invalidateQueries({ queryKey: ["priceAlarms"] });
      toast.success(`Watching $${alarm.target_price} on ${alarm.symbol} again.`);
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Could not re-arm the price alarm."));
    },
    onSettled: () => setPendingPriceAlarmId(null),
  });

  const deletePriceAlarmMutation = useMutation({
    mutationFn: (alarmId: number) => api.deletePriceAlarm(alarmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["priceAlarms"] });
      toast.success("Price alarm removed.");
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Could not remove the price alarm."));
    },
    onSettled: () => setPendingPriceAlarmId(null),
  });

  const refreshMutation = useMutation({
    mutationFn: ({ symbol, token }: { symbol: string; token: string }) =>
      api.companionRefresh(symbol, token),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(["companionAgents", variables.symbol], data);
      queryClient.invalidateQueries({ queryKey: ["companionStatus"] });
      setAdminOpen(false);
      toast.success(`Companion agents refreshed for ${variables.symbol}.`);
    },
    onError: (error) => {
      toast.error(errorMessage(error, "Could not refresh companion agents."));
    },
  });

  const indicators = useMemo(
    () => activeIndicators.filter((key) => getIndicatorDefinition(key)),
    [activeIndicators],
  );

  const selectSymbol = useCallback(
    (symbol: string) => {
      if (!symbol) {
        return;
      }
      setSelectedSymbol(symbol);
      setRecents((current) =>
        [symbol, ...current.filter((item) => item !== symbol)].slice(0, 10),
      );
    },
    [setSelectedSymbol, setRecents],
  );

  // Warm the cache on hover so clicking a symbol feels instant.
  const prefetchSymbol = useCallback(
    (symbol: string) => {
      if (!symbol || symbol === selectedSymbol) {
        return;
      }
      queryClient.prefetchQuery({
        queryKey: ["symbol", symbol],
        queryFn: () => api.symbol(symbol),
      });
      queryClient.prefetchQuery({
        queryKey: ["chart", symbol, range],
        queryFn: () => api.chart(symbol, range),
      });
    },
    [queryClient, selectedSymbol, range],
  );

  const addIndicator = (indicator: IndicatorKey) => {
    setActiveIndicators((current) =>
      current.includes(indicator) ? current : [...current, indicator],
    );
  };

  const removeIndicator = (indicator: IndicatorKey) => {
    setActiveIndicators((current) => current.filter((key) => key !== indicator));
  };

  const handleRefresh = () => {
    if (!bootstrapQuery.data?.companion_refresh_enabled || !selectedSymbol) {
      return;
    }
    setAdminOpen(true);
  };

  const submitRefresh = (token: string) => {
    if (!selectedSymbol) {
      return;
    }
    refreshMutation.mutate({ symbol: selectedSymbol, token });
  };

  const subscribedAgentKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const alarm of alarmsQuery.data?.items ?? []) {
      if (alarm.symbol === selectedSymbol) {
        keys.add(alarm.agent_key);
      }
    }
    return keys;
  }, [alarmsQuery.data, selectedSymbol]);

  const toggleAlarm = (agentKey: string, agentTitle: string, isSubscribed: boolean) => {
    if (!selectedSymbol) {
      return;
    }
    setPendingAlarmAgentKey(agentKey);
    if (isSubscribed) {
      const alarm = alarmsQuery.data?.items.find(
        (item) => item.symbol === selectedSymbol && item.agent_key === agentKey,
      );
      if (!alarm) {
        setPendingAlarmAgentKey(null);
        return;
      }
      deleteAlarmMutation.mutate(alarm.id, {
        onSuccess: () => toast.success(`Stopped emailing you for ${agentTitle} on ${selectedSymbol}.`),
      });
      return;
    }
    createAlarmMutation.mutate(
      { symbol: selectedSymbol, agentKey },
      {
        onSuccess: () =>
          toast.success(`You'll get an email when ${agentTitle} turns BUY or SELL on ${selectedSymbol}.`),
      },
    );
  };

  const subscribedEventTypes = useMemo(() => {
    const keys = new Set<string>();
    for (const alarm of eventAlarmsQuery.data?.items ?? []) {
      if (alarm.symbol === selectedSymbol) {
        keys.add(alarm.event_type);
      }
    }
    return keys;
  }, [eventAlarmsQuery.data, selectedSymbol]);

  // Only this symbol's alarms; the list is per-symbol like every other panel
  // section, while the query itself holds the user's alarms across all symbols.
  const symbolPriceAlarms = useMemo(() => {
    if (!selectedSymbol) return [];
    return (priceAlarmsQuery.data?.items ?? []).filter(
      (alarm) => alarm.symbol === selectedSymbol,
    );
  }, [priceAlarmsQuery.data, selectedSymbol]);

  const createPriceAlarm = (targetPrice: number) => {
    if (!selectedSymbol) return;
    createPriceAlarmMutation.mutate({ symbol: selectedSymbol, targetPrice });
  };

  const rearmPriceAlarm = (alarmId: number) => {
    setPendingPriceAlarmId(alarmId);
    rearmPriceAlarmMutation.mutate(alarmId);
  };

  const deletePriceAlarm = (alarmId: number) => {
    setPendingPriceAlarmId(alarmId);
    deletePriceAlarmMutation.mutate(alarmId);
  };

  const toggleEventAlarm = (eventType: string, eventTitle: string, isSubscribed: boolean) => {
    if (!selectedSymbol) {
      return;
    }
    setPendingEventAlarmType(eventType);
    if (isSubscribed) {
      const alarm = eventAlarmsQuery.data?.items.find(
        (item) => item.symbol === selectedSymbol && item.event_type === eventType,
      );
      if (!alarm) {
        setPendingEventAlarmType(null);
        return;
      }
      deleteEventAlarmMutation.mutate(alarm.id, {
        onSuccess: () => toast.success(`Stopped emailing you for ${eventTitle} on ${selectedSymbol}.`),
      });
      return;
    }
    createEventAlarmMutation.mutate(
      { symbol: selectedSymbol, eventType },
      {
        onSuccess: () =>
          toast.success(`You'll get an email when ${eventTitle} changes for ${selectedSymbol}.`),
      },
    );
  };

  const ranges = bootstrapQuery.data?.available_ranges ?? [];

  // Global keyboard shortcuts: Cmd/Ctrl-K or "/" opens search; digits switch range.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        !!target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen((open) => !open);
        return;
      }
      if (typing || paletteOpen || adminOpen) {
        return;
      }
      if (event.key === "/") {
        event.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (/^[1-9]$/.test(event.key)) {
        const next = ranges[Number(event.key) - 1];
        if (next) {
          event.preventDefault();
          setRange(next.key);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paletteOpen, adminOpen, ranges]);

  const summaryText = useMemo(
    () => companionSummaryText(companionStatusQuery.data, selectedSymbol),
    [companionStatusQuery.data, selectedSymbol],
  );

  const companionError =
    refreshMutation.error || companionAgentsQuery.error
      ? errorMessage(
          refreshMutation.error ?? companionAgentsQuery.error,
          "Could not load companion agents.",
        )
      : null;

  const agentSignalsError = agentSignalsQuery.error
    ? errorMessage(agentSignalsQuery.error, "Could not load agent signals.")
    : null;

  if (bootstrapQuery.isPending) {
    return (
      <div className="explorer-page">
        <div className="app-shell">
          <p className="chart-status" role="status">
            Loading…
          </p>
        </div>
      </div>
    );
  }

  if (bootstrapQuery.isError || !bootstrapQuery.data) {
    return (
      <div className="explorer-page">
        <div className="app-shell">
          <p className="chart-status is-error" role="status">
            {errorMessage(bootstrapQuery.error, "The app could not start.")}
          </p>
        </div>
      </div>
    );
  }

  const bootstrap = bootstrapQuery.data;
  const symbolData = symbolQuery.data;
  const chartData = chartQuery.data ?? null;
  const chartIsError = chartQuery.isError;
  const chartStatus = chartIsError
    ? errorMessage(chartQuery.error, "Could not load chart.")
    : chartQuery.isFetching
      ? "Loading chart..."
      : "";

  return (
    <div className="explorer-page">
      <div className="app-progress" data-active={globalFetching > 0 ? "true" : "false"} aria-hidden />
      <div className="app-shell">
        <TopBar user={bootstrap.user} onSearch={() => setPaletteOpen(true)} />
        <main className="explorer-grid">
          <Watchlist
            trackedSymbols={bootstrap.tracked_symbols}
            recentSymbols={recents}
            selectedSymbol={selectedSymbol}
            onSelect={selectSymbol}
            onHover={prefetchSymbol}
          />
          <MarketPanel
            companyName={symbolData?.company_name || "Tracked symbol"}
            symbol={selectedSymbol ?? "--"}
            chart={chartIsError ? null : chartData}
            chartStatus={chartStatus}
            chartIsError={chartIsError}
            chartLoading={chartQuery.isPending}
            agentResult={symbolData?.agent_result ?? null}
            resolvedTheme={resolvedTheme}
            ranges={bootstrap.available_ranges}
            activeRange={range}
            onRangeSelect={setRange}
            chartType={chartType}
            onChartTypeChange={setChartType}
            activeIndicators={indicators}
            onAddIndicator={addIndicator}
            onRemoveIndicator={removeIndicator}
          />
          <AgentPanel
            agentResult={symbolData?.agent_result ?? null}
            lastRun={symbolData?.last_run ?? bootstrap.last_run}
          >
            <CompanionSection
              summaryText={summaryText}
              agents={companionAgentsQuery.data}
              isLoading={companionAgentsQuery.isFetching}
              isRefreshing={refreshMutation.isPending}
              errorMessage={companionError}
              refreshEnabled={bootstrap.companion_refresh_enabled}
              refreshBusy={refreshMutation.isPending}
              onRefresh={handleRefresh}
            />
            <AgentSignalsSection
              signals={agentSignalsQuery.data}
              isLoading={agentSignalsQuery.isFetching}
              errorMessage={agentSignalsError}
              alarmEmailEnabled={alarmsQuery.data?.email_alerts_enabled ?? bootstrap.alarm_email_enabled}
              subscribedAgentKeys={subscribedAgentKeys}
              pendingAgentKey={pendingAlarmAgentKey}
              onToggleAlarm={toggleAlarm}
            />
            <SymbolEventAlarmsSection
              symbol={selectedSymbol}
              eventTypes={bootstrap.event_alarm_types}
              alarmEmailEnabled={eventAlarmsQuery.data?.email_alerts_enabled ?? bootstrap.alarm_email_enabled}
              subscribedEventTypes={subscribedEventTypes}
              pendingEventType={pendingEventAlarmType}
              onToggleAlarm={toggleEventAlarm}
            />
            <SymbolPriceAlarmsSection
              symbol={selectedSymbol}
              alarms={symbolPriceAlarms}
              alarmEmailEnabled={priceAlarmsQuery.data?.email_alerts_enabled ?? bootstrap.alarm_email_enabled}
              referencePrice={chartData?.summary.last_close ?? null}
              pendingAlarmId={pendingPriceAlarmId}
              isCreating={createPriceAlarmMutation.isPending}
              onCreate={createPriceAlarm}
              onRearm={rearmPriceAlarm}
              onDelete={deletePriceAlarm}
            />
          </AgentPanel>
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        symbols={bootstrap.tracked_symbols}
        onClose={() => setPaletteOpen(false)}
        onSelect={selectSymbol}
        onHover={prefetchSymbol}
      />
      <AdminTokenDialog
        open={adminOpen}
        symbol={selectedSymbol ?? "--"}
        busy={refreshMutation.isPending}
        onClose={() => setAdminOpen(false)}
        onSubmit={submitRefresh}
      />
    </div>
  );
}
